import React, { useEffect, useRef, useState } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';

export type IdCardSide = 'front' | 'back';

export type IdCardSides = {
  front: string | null;
  back: string | null;
};

export const emptyIdCardSides = (): IdCardSides => ({ front: null, back: null });

export function hasAnyIdCardSide(sides: IdCardSides | null | undefined): boolean {
  return !!(sides?.front || sides?.back);
}

export function hasRequiredIdCardFront(sides: IdCardSides | null | undefined): boolean {
  return !!sides?.front;
}

const SIDE_LABELS: Record<IdCardSide, string> = {
  front: 'Recto',
  back: 'Verso',
};

interface SideSlotProps {
  side: IdCardSide;
  photo: string | null;
  onRequestDelete: () => void;
  onPhoto: (dataUrl: string) => void;
  required?: boolean;
}

const SideSlot: React.FC<SideSlotProps> = ({ side, photo, onRequestDelete, onPhoto, required }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [camError, setCamError] = useState<string | null>(null);

  useEffect(() => {
    if (!cameraOpen) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (video && stream) {
      video.srcObject = stream;
      video.play().catch(() => {});
    }
  }, [cameraOpen]);

  useEffect(
    () => () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  const openCamera = async () => {
    setCamError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      setCamError("Impossible d'accéder à la caméra — utilisez « Importer ».");
    }
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    onPhoto(canvas.toDataURL('image/jpeg', 0.85));
    closeCamera();
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onPhoto(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {SIDE_LABELS[side]}
          {required ? <span className="text-red-500 normal-case tracking-normal"> *</span> : null}
        </p>
        {photo && (
          <button
            type="button"
            onClick={onRequestDelete}
            className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors"
          >
            <X className="w-3 h-3" /> Supprimer
          </button>
        )}
      </div>
      {camError && <p className="text-xs text-orange-600">{camError}</p>}
      {photo ? (
        <div className="border rounded-lg overflow-hidden bg-white">
          <img src={photo} alt={`${SIDE_LABELS[side]} pièce d'identité`} className="w-full object-contain max-h-36" />
        </div>
      ) : cameraOpen ? (
        <div className="space-y-2">
          <div className="rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
          </div>
          <canvas ref={canvasRef} className="hidden" />
          <div className="flex gap-2">
            <button type="button" onClick={capture} className="btn-primary flex items-center gap-2 flex-1 text-sm py-2">
              <Camera className="w-4 h-4" /> Capturer
            </button>
            <button
              type="button"
              onClick={closeCamera}
              className="px-3 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm flex items-center gap-1"
            >
              <X className="w-4 h-4" /> Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openCamera}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors text-xs"
          >
            <Camera className="w-3.5 h-3.5" /> Caméra
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors text-xs"
          >
            <Upload className="w-3.5 h-3.5" /> Importer
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFile}
          />
        </div>
      )}
    </div>
  );
};

export interface IdCardCaptureProps {
  label: string;
  required?: boolean;
  sides: IdCardSides;
  onSidesChange: (sides: IdCardSides) => void;
}

/** Capture recto + verso for one person's identity card. */
export const IdCardCapture: React.FC<IdCardCaptureProps> = ({ label, required, sides, onSidesChange }) => {
  const [pendingDeleteSide, setPendingDeleteSide] = useState<IdCardSide | null>(null);

  const setSide = (side: IdCardSide, dataUrl: string | null) => {
    onSidesChange({ ...sides, [side]: dataUrl });
  };

  const confirmDelete = () => {
    if (!pendingDeleteSide) return;
    setSide(pendingDeleteSide, null);
    setPendingDeleteSide(null);
  };

  return (
    <div className="card border border-gray-200 space-y-3">
      <h3 className="font-bold text-sm flex items-center gap-2 flex-wrap">
        {label}
        {required ? (
          <span className="text-xs font-normal text-red-500">(recto obligatoire — mineur)</span>
        ) : (
          <span className="text-xs font-normal text-gray-400">(optionnel)</span>
        )}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SideSlot
          side="front"
          photo={sides.front}
          required={required}
          onPhoto={(v) => setSide('front', v)}
          onRequestDelete={() => setPendingDeleteSide('front')}
        />
        <SideSlot
          side="back"
          photo={sides.back}
          onPhoto={(v) => setSide('back', v)}
          onRequestDelete={() => setPendingDeleteSide('back')}
        />
      </div>

      <ConfirmModal
        isOpen={pendingDeleteSide !== null}
        title="Supprimer cette image ?"
        message={
          pendingDeleteSide
            ? `Vous êtes sur le point de supprimer le ${SIDE_LABELS[pendingDeleteSide].toLowerCase()} de la pièce d'identité. Cette action est irréversible.`
            : ''
        }
        confirmLabel="Supprimer l'image"
        cancelLabel="Annuler"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onClose={() => setPendingDeleteSide(null)}
      />
    </div>
  );
};
